import { useEffect, useRef, useState } from 'react'

import { useRouterState } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { auth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  clearSessionAndRedirectToLogin,
  getTokenSecondsRemaining,
  SESSION_WARNING_SECONDS
} from '@/lib/axios'

function formatCountdown(seconds: number) {
  const safeSeconds = Math.max(0, seconds)
  const mins = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function getWindowPathname() {
  return typeof window !== 'undefined' ? window.location.pathname : ''
}

/**
 * Di halaman /report, jangan panggil useRouterState.
 * Router v1.17x di mode development bisa crash: null.isServer
 */
export default function SessionExpiryWarning() {
  const [pathname, setPathname] = useState(getWindowPathname)

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname)
    sync()
    window.addEventListener('popstate', sync)
    return () => window.removeEventListener('popstate', sync)
  }, [])

  if (pathname.startsWith('/report')) {
    return null
  }

  return <SessionExpiryWarningInner />
}

function SessionExpiryWarningInner() {
  const pathname = useRouterState({ select: s => s.location.pathname })
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [isExtending, setIsExtending] = useState(false)
  const isRedirectingRef = useRef(false)

  const isAuthRoute = pathname.startsWith('/auth')
  const isWarningVisible =
    !isAuthRoute &&
    secondsLeft !== null &&
    secondsLeft > 0 &&
    secondsLeft <= SESSION_WARNING_SECONDS

  useEffect(() => {
    if (isAuthRoute) {
      setSecondsLeft(null)
      return
    }

    const redirectToLogin = () => {
      if (isRedirectingRef.current) return
      isRedirectingRef.current = true
      clearSessionAndRedirectToLogin()
    }

    const tick = () => {
      const remaining = getTokenSecondsRemaining()

      if (remaining === null) {
        setSecondsLeft(null)
        return
      }

      if (remaining <= 0) {
        setSecondsLeft(0)
        redirectToLogin()
        return
      }

      setSecondsLeft(remaining)
    }

    tick()
    const intervalId = window.setInterval(tick, 1000)

    return () => window.clearInterval(intervalId)
  }, [isAuthRoute])

  const handleContinueSession = async () => {
    if (isExtending) return

    setIsExtending(true)
    try {
      const newToken = await auth.refreshToken()
      if (!newToken) {
        toast.error('Gagal memperpanjang sesi. Silakan login kembali.')
        if (!isRedirectingRef.current) {
          isRedirectingRef.current = true
          clearSessionAndRedirectToLogin()
        }
        return
      }

      toast.success('Sesi berhasil diperpanjang')
      setSecondsLeft(getTokenSecondsRemaining(newToken))
    } catch {
      toast.error('Gagal memperpanjang sesi. Silakan login kembali.')
      if (!isRedirectingRef.current) {
        isRedirectingRef.current = true
        clearSessionAndRedirectToLogin()
      }
    } finally {
      setIsExtending(false)
    }
  }

  const handleGoToLogin = () => {
    if (isRedirectingRef.current) return
    isRedirectingRef.current = true
    clearSessionAndRedirectToLogin()
  }

  return (
    <Dialog
      open={isWarningVisible}
      onOpenChange={() => {
        // Modal hanya bisa ditutup lewat aksi tombol / expiry redirect
      }}
    >
      <DialogContent
        centered
        isHideCloseButton
        className="sm:max-w-[420px]"
        onInteractOutside={event => event.preventDefault()}
        onEscapeKeyDown={event => event.preventDefault()}
        onPointerDownOutside={event => event.preventDefault()}
      >
        <DialogHeader className="items-center text-center sm:items-center sm:text-center">
          <DialogTitle>Sesi Akan Berakhir</DialogTitle>
          <DialogDescription>
            Sesi Anda akan segera berakhir. Lanjutkan sesi untuk tetap bekerja,
            atau kembali ke halaman login.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-1 py-2">
          <p className="text-muted-foreground text-sm">Sisa waktu</p>
          <p className="text-primary font-mono text-4xl font-semibold tracking-wider">
            {formatCountdown(secondsLeft ?? 0)}
          </p>
        </div>

        <DialogFooter className="mt-2 sm:justify-center">
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={handleGoToLogin}
              disabled={isExtending}
            >
              Ke Halaman Login
            </Button>
            <Button
              type="button"
              onClick={handleContinueSession}
              disabled={isExtending}
            >
              {isExtending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Memperpanjang...
                </>
              ) : (
                'Lanjutkan Sesi'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
